import { environment } from "../config/environment";
import { getFromBackendAPI, postToBackendAPI, patchToBackendAPI } from "./api.service";

/**
 * Discussion status constants
 */
export const DISCUSSION_STATUS = {
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  RESOLVED: "resolved",
} as const;

export type DiscussionStatus = typeof DISCUSSION_STATUS[keyof typeof DISCUSSION_STATUS];

/**
 * Discussion DTOs - matching backend API models
 */
export interface DiscussionDto {
  id: number;
  spreadsheetId: string;
  title: string;
  type: string;
  tags?: string[];
  status?: string;
  description?: string;
  descriptionContent?: string;
  createdByEmail: string;
  createdByName: string;
  createdByRole?: string;
  taggedUserEmails?: string[];
  createdAt: string;
  updatedAt: string;
  isDeleted?: boolean;
  commentCount: number;
  statusItemIds: number[];
  isArchived?: boolean;
}

export interface DiscussionCommentDto {
  id: number;
  discussionId: number;
  commentText: string;
  commentContent?: string;
  createdByEmail: string;
  createdByName: string;
  createdByRole?: string;
  statusBefore?: string;
  statusAfter?: string;
  taggedUserEmails?: string[];
  createdAt: string;
  updatedAt: string;
  isArchived: boolean;
}

export interface CreateCommentRequest {
  commentText: string;
  commentContent?: string;
  createdByRole?: string;
  taggedUserEmails?: string[];
  newDiscussionStatus?: string;
}

export interface UpdateCommentRequest {
  commentText: string;
  commentContent?: string;
  taggedUserEmails?: string[];
}

export interface CreateDiscussionRequest {
  spreadsheetId: string;
  title: string;
  type: string;
  tags?: string[];
  status?: string;
  description?: string;
  descriptionContent?: string;
  createdByRole?: string;
  taggedUserEmails?: string[];
  statusItemIds?: number[];
  /**
   * When creating a review_point with linked status items, controls whether
   * those items are auto-flipped to "Rework Required" on the server. Omitting
   * the field (or passing true) preserves the default behaviour; pass false
   * to keep the status items at their current status. Ignored by the server
   * for non-review_point types and for discussions with no linked items.
   */
  setLinkedStatusItemsToReworkRequired?: boolean;
}

export interface UpdateDiscussionRequest {
  title: string;
  description?: string;
  descriptionContent?: string;
  tags?: string[];
  taggedUserEmails?: string[];
  statusItemIds?: number[];
}

/**
 * Get all discussions for a given spreadsheet
 * @param spreadsheetId - The spreadsheet ID (GUID)
 * @returns Promise with array of discussions
 */
export async function getDiscussionsBySpreadsheet(
  spreadsheetId: string
): Promise<DiscussionDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/spreadsheet/${spreadsheetId}`;
  return await getFromBackendAPI<DiscussionDto[]>(endpoint);
}

/**
 * Get all comments for a given discussion
 * @param discussionId - The discussion ID
 * @returns Promise with array of comments
 */
export async function getCommentsByDiscussion(discussionId: number): Promise<DiscussionCommentDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}/comments`;
  return await getFromBackendAPI<DiscussionCommentDto[]>(endpoint);
}

/**
 * Create a new comment on a discussion
 * @param discussionId - The discussion ID
 * @param comment - The comment data to create
 * @returns Promise with the created comment
 */
export async function createComment(discussionId: number, comment: CreateCommentRequest): Promise<DiscussionCommentDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}/comments`;
  return await postToBackendAPI<DiscussionCommentDto>(endpoint, comment);
}

/**
 * Update an existing comment
 * @param discussionId - The discussion ID
 * @param commentId - The comment ID to update
 * @param comment - The updated comment data
 * @returns Promise with the updated comment
 */
export async function updateComment(
  discussionId: number,
  commentId: number,
  comment: UpdateCommentRequest
): Promise<DiscussionCommentDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}/comments/${commentId}`;
  return await patchToBackendAPI<DiscussionCommentDto>(endpoint, comment);
}

/**
 * Archive a comment (one-way, author only)
 * @param discussionId - The discussion ID
 * @param commentId - The comment ID to archive
 * @returns Promise with the archived comment DTO
 */
export async function archiveComment(
  discussionId: number,
  commentId: number
): Promise<DiscussionCommentDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}/comments/${commentId}/archive`;
  return await patchToBackendAPI<DiscussionCommentDto>(endpoint, {});
}

/**
 * Create a new discussion
 * @param discussion - The discussion data to create
 * @returns Promise with the created discussion
 */
export async function createDiscussion(discussion: CreateDiscussionRequest): Promise<DiscussionDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}`;
  return await postToBackendAPI<DiscussionDto>(endpoint, discussion);
}

/**
 * Update an existing discussion
 * @param discussionId - The discussion ID to update
 * @param discussion - The updated discussion data
 * @returns Promise with the updated discussion
 */
export async function updateDiscussion(
  discussionId: number,
  discussion: UpdateDiscussionRequest
): Promise<DiscussionDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}`;
  return await patchToBackendAPI<DiscussionDto>(endpoint, discussion);
}

/**
 * Archive a discussion (one-way, author only)
 * @param discussionId - The discussion ID to archive
 * @returns Promise with the archived discussion DTO
 */
export async function archiveDiscussion(discussionId: number): Promise<DiscussionDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.discussions}/${discussionId}/archive`;
  return await patchToBackendAPI<DiscussionDto>(endpoint, {});
}

